// ── v1.1 Privacy Policy + Terms (LEGAL_PAGES_ENABLED) — DRAFT, owner to review ─
//
// Plain-language drafts written for India's Digital Personal Data Protection Act, 2023
// (DPDP) basics: what is collected and why, consent, rights (access, correction, erasure,
// grievance), processors, retention, children. They are NOT legal advice and must be
// reviewed by the owner / counsel before marketing. While the flag is off these routes
// redirect to "/" exactly like any unknown path in r2b.
import { Link, Navigate, useLocation } from "react-router-dom";
import { T } from "../tokens";
import { useLaunchConfig } from "../lib/launch-config";
import { usePageMeta } from "../lib/page-meta";
import { LegalLinks } from "../components/LegalLinks";

function Shell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <main style={{ maxWidth: "760px", margin: "0 auto", padding: "32px 16px 48px", color: T.t1, fontFamily: T.body, fontSize: "14px", lineHeight: 1.7 }}>
      <p role="note" data-testid="draft-banner" style={{ background: "#3A2A10", color: "#F2B54A", border: "1px solid #6B4E1A", borderRadius: "8px", padding: "10px 14px", fontWeight: 600, margin: "0 0 20px" }}>
        DRAFT — owner to review. This text has not been reviewed by a lawyer and is not yet in force.
      </p>
      <p style={{ margin: "0 0 8px" }}><Link to="/" style={{ color: T.teal }}>← Calibre</Link></p>
      <h1 style={{ fontFamily: T.display, color: T.t0, fontSize: "26px", margin: "0 0 4px" }}>{title}</h1>
      <p style={{ color: T.t3, fontSize: "12px", margin: "0 0 20px" }}>Version 2026-11 draft 1 · DevAscend</p>
      {children}
      <LegalLinks />
    </main>
  );
}
const H = ({ children }: { children: React.ReactNode }) => <h2 style={{ fontFamily: T.display, color: T.t0, fontSize: "17px", margin: "22px 0 6px" }}>{children}</h2>;

export function PrivacyPage() {
  const { flags, supportEmail, ready } = useLaunchConfig();
  usePageMeta("Privacy Policy (draft) — Calibre", "How Calibre collects, uses and protects your personal data.");
  if (!ready) return null;
  if (!flags.legalPages) return <Navigate to="/" replace />;
  const contact = supportEmail ?? "[owner: add a grievance contact e-mail]";
  return (
    <Shell title="Privacy Policy">
      <H>Who we are</H>
      <p>Calibre is a job-matching service run by DevAscend ("we"). For India's Digital Personal Data Protection Act, 2023, DevAscend is the Data Fiduciary for the personal data you give us.</p>
      <H>What we collect</H>
      <ul>
        <li><b>Account:</b> your name, e-mail address and password (stored only as a secure hash), or your Google account name, e-mail and picture if you sign in with Google.</li>
        <li><b>Résumé:</b> the text of the résumé you upload and the details we read from it (job titles, skills, experience, education, location preferences). We do not keep the uploaded file itself.</li>
        <li><b>Your activity:</b> the jobs you save, mark as applied or skip, and jobs you capture with the Calibre browser extension (job details only — never anything else from the page).</li>
        <li><b>Usage statistics:</b> page visits and a few product events (for example "résumé uploaded"), counted without cookies and without your name or e-mail.</li>
      </ul>
      <H>Why we use it</H>
      <p>Only to give you the service: to read your résumé, find and score matching jobs, show your tracker, send account e-mails (verification, password reset), keep the service safe from abuse, and understand which features are used. We do not sell your data and we do not apply to jobs on your behalf.</p>
      <H>Consent</H>
      <p>We process your data because you agree to it when you create an account. You can withdraw consent at any time by deleting your account (Profile → Delete account). Withdrawing consent does not affect processing that already happened.</p>
      <H>Who processes it for us</H>
      <p>We use service providers who process data only on our instructions: Anthropic (reading your résumé), OpenAI (turning text into the numbers we use to compare jobs), Brevo (sending e-mails), Plausible (cookie-less usage statistics), Sentry (error reports, when enabled) and our hosting provider. Some of these providers process data outside India. If you joined Calibre through a partner (for example ExH1B), we share your matches with that partner as agreed when you signed up there.</p>
      <H>How long we keep it</H>
      <p>As long as your account exists. When you delete your account, your résumé, matches, tracker, captured jobs and consent records are deleted. Anonymous cost records with no link to you may be kept for accounting.</p>
      <H>Your rights</H>
      <ul>
        <li><b>Access:</b> {flags.dataExport
          ? <>download a copy of your account, résumés, matches, tracker, captured jobs and consent records from Profile → Download my data, or write to {contact} for anything else we hold.</>
          : <>write to {contact} and we will send you a copy of your data.</>}</li>
        <li><b>Correction:</b> update your profile or upload a new résumé at any time.</li>
        <li><b>Erasure:</b> delete your account from Profile → Delete account.</li>
        <li><b>Grievances:</b> write to {contact}. We aim to reply within 7 days. If you are not satisfied you may approach the Data Protection Board of India.</li>
      </ul>
      <H>Children</H>
      <p>Calibre is for people aged 18 or over. Do not create an account if you are under 18.</p>
      <H>Changes</H>
      <p>If we change this policy we will update the version above and tell signed-in users before the change applies.</p>
    </Shell>
  );
}

export function TermsPage() {
  const { flags, supportEmail, ready } = useLaunchConfig();
  usePageMeta("Terms of Use (draft) — Calibre", "The rules for using Calibre.");
  if (!ready) return null;
  if (!flags.legalPages) return <Navigate to="/" replace />;
  return (
    <Shell title="Terms of Use">
      <H>The service</H>
      <p>Calibre reads your résumé and suggests jobs with a match score. It is in Beta: it works best for technology roles in India, is less tested for other fields, and its suggestions can be incomplete or wrong. Scores are an automated estimate, not advice or a promise of an interview or offer.</p>
      <H>Your account</H>
      <p>You must be 18 or over, give accurate details, keep your password safe and use the service only for your own job search. One account per person.</p>
      <H>What you may not do</H>
      <p>Do not upload someone else's résumé, try to break or overload the service, scrape it, or use it to send spam. We may suspend accounts that do.</p>
      <H>Job listings</H>
      <p>Jobs come from public sources and employers' sites. We do not employ, recruit for, or vouch for any employer, and we never apply on your behalf. Always check a job on the employer's own site before you apply.</p>
      <H>Free service</H>
      <p>The free tier costs nothing. Daily limits apply to résumé uploads and captures to keep the service available for everyone.</p>
      <H>Liability</H>
      <p>The service is provided "as is". To the extent the law allows, DevAscend is not liable for decisions you make based on Calibre's suggestions.</p>
      <H>Ending</H>
      <p>You can delete your account at any time. We may end the Beta or change these terms; we will tell signed-in users before a change applies.</p>
      <H>Contact</H>
      <p>{supportEmail ? <>Questions: <a href={`mailto:${supportEmail}`} style={{ color: T.teal }}>{supportEmail}</a>.</> : "[owner: add a contact e-mail]"} These terms are governed by the laws of India.</p>
    </Shell>
  );
}

/** Member-only app paths. A signed-out visitor (expired session, shared link) on one of
 *  these is sent to "/" exactly as in r2b — it is not a missing page. */
export const MEMBER_PATHS = ["/matches", "/queue", "/profile", "/billing", "/admin", "/extensions"];
export function isMemberPath(pathname: string): boolean {
  return MEMBER_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export function NotFoundPage() {
  const { flags, ready } = useLaunchConfig();
  const { pathname } = useLocation();
  usePageMeta("Page not found — Calibre");
  if (!ready) return null;
  if (!flags.reliability || isMemberPath(pathname)) return <Navigate to="/" replace />;
  return (
    <main data-testid="not-found" style={{ maxWidth: "560px", margin: "80px auto", padding: "0 16px", textAlign: "center", fontFamily: T.body, color: T.t1 }}>
      <h1 style={{ fontFamily: T.display, color: T.t0, fontSize: "28px", margin: "0 0 8px" }}>Page not found</h1>
      <p style={{ color: T.t2, margin: "0 0 18px" }}>That page doesn't exist or has moved.</p>
      <Link to="/" style={{ color: T.teal, fontWeight: 600 }}>Go to Calibre</Link>
      <LegalLinks />
    </main>
  );
}
